import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import { all_routes } from "../../../router/all_routes";
import { gender } from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useCourse } from "../../../configuration/course/hooks/useCourse";
import { useCenter } from "../../centers/hooks/useCenter";
import { useQuickSaveStudentMutation, useSendWhatsappMutation } from "../api/studentApi";
import { showToast } from "../../../../core/utils/alert";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { selectUserRole, selectCurrentUser } from "../../../../core/data/slice/authSlice";
import { useActiveSession } from "../../../../utils/getActiveSession";
import { useBatchTiming } from "../../../configuration/batch-timing/hooks/useBatchTiming";
import ShareLinkModal from "./ShareLinkModal";
import StarterKitNoticeModal from "./StarterKitNoticeModal";
import PhotoCaptureCropper from "../../../../core/common/photoCaptureCropper";
import { encryptId } from "../../../../core/utils/crypto";

const DATE_PICKER_POPUP_STYLE = { zIndex: 1060 };
const disableFutureDates = (current: dayjs.Dayjs) =>
  Boolean(current && current.isAfter(dayjs().endOf("day")));
const disableDojDates = (current: dayjs.Dayjs, isSuperAdmin: boolean = false) => {
  if (!current) return false;
  if (current.isAfter(dayjs().endOf("day"))) return true;
  if (!isSuperAdmin) {
    const minDojDate = dayjs().subtract(10, "day").startOf("day");
    if (current.isBefore(minDojDate)) return true;
  }
  return false;
};

const classOptions = [
  { value: "UKG", label: "UKG" },
  { value: "1st", label: "1st" },
  { value: "2nd", label: "2nd" },
  { value: "3rd", label: "3rd" },
  { value: "4th", label: "4th" },
  { value: "5th", label: "5th" },
  { value: "6th", label: "6th" },
  { value: "7th", label: "7th" },
  { value: "8th", label: "8th" },
  { value: "9th", label: "9th" },
  { value: "10th", label: "10th" },
];

const getInitialForm = () => ({
  full_name: "",
  fathers_name: "",
  gender: "",
  course: "",
  phone: "",
  class_name: "",
  center_id: "",
  batch_id: null,
  date_of_join: dayjs().format("YYYY-MM-DD"),
});

const QuickRegister = () => {
  const routes = all_routes;
  const navigate = useNavigate();

  const userRole = useSelector(selectUserRole) || "";
  const currentUser = useSelector(selectCurrentUser);
  const isSuperAdmin = userRole === "superadmin";

  const activeSession = useActiveSession();
  const sessionId = activeSession?.id || 0;

  const { getCourseState } = useCourse({ published: true });
  const { courses } = getCourseState();

  const courseOptions = useMemo(
    () =>
      courses
        .filter((course: any) => course !== null)
        .map((course: any) => ({
          value: course.id.toString(),
          label: course.name,
          id: course.id,
        })),
    [courses]
  );

  const [formData, setFormData] = useState(getInitialForm());
  const [photo, setPhoto] = useState<File | null>(null);
  const [errors, setErrors] = useState<Partial<ReturnType<typeof getInitialForm>>>({});

  const { batchTimings = [], isLoading: isBatchTimingsLoading } = useBatchTiming({ size: 1000 });

  const filteredBatchTimings = useMemo(
    () => {
      if (!formData.course) {
        return [];
      }
      return batchTimings.filter((batch: any) => {
        const courseMatch = batch.course_id.toString() === formData.course;
        const centerMatch = isSuperAdmin
          ? batch.center_id.toString() === formData.center_id
          : true;
        return courseMatch && centerMatch;
      });
    },
    [batchTimings, formData.center_id, formData.course, isSuperAdmin]
  );

  const batchTimingOptions = useMemo(
    () =>
      filteredBatchTimings
        .filter((batch: any) => batch !== null)
        .map((batch: any) => ({
          value: batch.id.toString(),
          label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
          description: `[${batch.days?.join(", ")}]`,
        })),
    [filteredBatchTimings]
  );

  const selectedCourse = useMemo(
    () => courses.find((course: any) => course && course.id.toString() === formData.course),
    [courses, formData.course]
  );

  // Center list — only needed for super admin
  const { useCenterList } = useCenter({ skip: !isSuperAdmin });
  const { data: centerResponse } = useCenterList();
  const centerOptions = useMemo(
    () =>
      (centerResponse?.data?.items || [])
        .filter((c: any) => c !== null)
        .map((c: any) => ({ value: c.id.toString(), label: c.name })),
    [centerResponse]
  );


  const [quickSaveStudent, { isLoading: isRegistering }] = useQuickSaveStudentMutation();
  const [sendWhatsapp, { isLoading: isSendingWhatsapp }] = useSendWhatsappMutation();

  const [showShareModal, setShowShareModal] = useState(false);
  const [newStudentId, setNewStudentId] = useState<string | null>(null);
  const [registrationLink, setRegistrationLink] = useState<string | null>(null);
  const [showKitNoticeModal, setShowKitNoticeModal] = useState(false);
  const [kitNoticeMessage, setKitNoticeMessage] = useState<string>("");
  const [registeredStudentName, setRegisteredStudentName] = useState<string>("");
  const [registeredCourseName, setRegisteredCourseName] = useState<string>("");
  const [isKitPending, setIsKitPending] = useState(false);

  // Capitalize first letter of each word
  const capitalizeWords = (str: string): string => {
    return str.replace(/(^|\s)\S/g, (char) => char.toUpperCase());
  };

  const handleInputChange = (field: string, value: string) => {
    if (["full_name", "fathers_name"].includes(field)) {
      value = capitalizeWords(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };



  const validate = (): boolean => {
    const newErrors: Partial<ReturnType<typeof getInitialForm>> = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
    if (!formData.fathers_name.trim()) newErrors.fathers_name = "Father's name is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.course) newErrors.course = "Course is required";
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.class_name.trim()) newErrors.class_name = "Class is required";
    if (isSuperAdmin && !formData.center_id) newErrors.center_id = "Center is required";
    if (formData.date_of_join && !isSuperAdmin) {
      const doj = dayjs(formData.date_of_join).startOf("day");
      const minDoj = dayjs().subtract(10, "day").startOf("day");
      const today = dayjs().endOf("day");
      if (doj.isAfter(today)) {
        showToast({
          title: "Validation Error",
          text: "Date of Joining cannot be in the future.",
          icon: "warning",
        });
        return false;
      }
      if (doj.isBefore(minDoj)) {
        showToast({
          title: "Validation Error",
          text: "Date of Joining cannot be older than 10 days in the past.",
          icon: "warning",
        });
        return false;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const defaultStageId = selectedCourse?.courseStages?.[0]?.id || 0;
      const finalCenterId = isSuperAdmin ? Number(formData.center_id) : (currentUser?.center_id ?? 0);

      const response = await quickSaveStudent({
        name: formData.full_name.trim(),
        fathers_name: formData.fathers_name.trim(),
        gender: formData.gender,
        course_id: Number(formData.course),
        stage_id: Number(defaultStageId),
        session_id: sessionId,
        phone: formData.phone.trim(),
        class: formData.class_name.trim(),
        center_id: finalCenterId,
        batch_id: formData.batch_id ? Number(formData.batch_id) : null,
        ...(formData.date_of_join ? { registration_date: formData.date_of_join } : {}),
        ...(photo ? { photo } : {}),
      }).unwrap();

      setFormData(getInitialForm());
      setPhoto(null);
      setErrors({});

      const starterKit = response.data?.starter_kit;
      const createdId = response.data?.student?.id;
      const studentName = response.data?.student?.name ?? formData.full_name.trim();
      const studentPhone = formData.phone.trim();
      const courseName = selectedCourse?.name || "";

      setRegisteredStudentName(studentName);
      setRegisteredCourseName(courseName);

      const kitNotAssigned = starterKit && starterKit.assigned === false;
      setIsKitPending(Boolean(kitNotAssigned));

      if (createdId) {
        // Generate the full registration link on the frontend (single source of truth)
        const genRegistrationLink = `${window.location.origin}/complete-registration/${encryptId(createdId.toString())}`;

        // Send WhatsApp automatically with the frontend-generated link
        try {
          await sendWhatsapp({
            phone: studentPhone,
            name: studentName,
            registration_link: genRegistrationLink,
          }).unwrap();
        } catch (err) {
          console.error("WhatsApp send failed:", err);
        }

        setNewStudentId(createdId.toString());
        setRegistrationLink(genRegistrationLink);

        if (kitNotAssigned) {
          setKitNoticeMessage(starterKit.message || "Starter kit stock is empty for this course.");
          setShowKitNoticeModal(true);
        } else {
          showToast({
            text: starterKit?.assigned
              ? "Student registered and starter kit assigned successfully!"
              : "Student registered successfully!",
            icon: "success",
          });
          setShowShareModal(true);
        }
      } else {
        if (kitNotAssigned) {
          setKitNoticeMessage(starterKit.message || "Starter kit stock is empty for this course.");
          setShowKitNoticeModal(true);
        } else {
          showToast({ text: "Student registered successfully!", icon: "success" });
          navigate(routes.studentList);
        }
      }
    } catch (error: any) {
      if (error?.status === 422 && error?.data?.error) {
        const apiErrors = error.data.error;
        const newErrors: any = {};
        
        const keyMapping: Record<string, string> = {
          name: 'full_name',
          course_id: 'course',
          class: 'class_name'
        };

        Object.keys(apiErrors).forEach((key) => {
          const formKey = keyMapping[key] || key;
          newErrors[formKey] = apiErrors[key][0];
        });
        setErrors((prev) => ({ ...prev, ...newErrors }));
      } else {
        showToast({ text: getApiErrorMessage(error) || "Failed to register student. Please try again.", icon: "error" });
      }
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Quick Register</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.studentList}>Students</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Quick Register
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
            <Link
              to={routes.addStudent}
              className="btn btn-outline-primary d-flex align-items-center mb-2"
            >
              <i className="ti ti-forms me-2" />
              Full Registration
            </Link>
            <Link
              to={routes.studentList}
              className="btn btn-primary d-flex align-items-center mb-2"
            >
              <i className="ti ti-list me-2" />
              Student List
            </Link>
          </div>
        </div>

        {/* Form Container */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header bg-light">
                <h4 className="card-title text-dark mb-0">Student Information</h4>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit} noValidate>
                  <div className="row">
                    {/* Full Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.full_name ? "is-invalid" : ""}`}
                        style={{ textTransform: "capitalize" }}
                        placeholder="Enter full name"
                        value={formData.full_name}
                        onChange={(e) => handleInputChange("full_name", e.target.value)}
                      />
                      {errors.full_name && (
                        <div className="invalid-feedback">{errors.full_name}</div>
                      )}
                    </div>

                    {/* Father's Name */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Father's Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.fathers_name ? "is-invalid" : ""}`}
                        style={{ textTransform: "capitalize" }}
                        placeholder="Enter father's name"
                        value={formData.fathers_name}
                        onChange={(e) => handleInputChange("fathers_name", e.target.value)}
                      />
                      {errors.fathers_name && (
                        <div className="invalid-feedback">{errors.fathers_name}</div>
                      )}
                    </div>

                    {/* Gender */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Gender <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className={`select ${errors.gender ? "is-invalid" : ""}`}
                        options={gender}
                        value={gender.find((opt) => opt.value === formData.gender) || null}
                        onChange={(val: any) => handleInputChange("gender", val?.value || "")}
                      />
                      {errors.gender && (
                        <div className="text-danger small mt-1">{errors.gender}</div>
                      )}
                    </div>

                    {/* Course */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Course <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className={`select ${errors.course ? "is-invalid" : ""}`}
                        placeholder="Select Course"
                        options={courseOptions}
                        value={courseOptions.find((opt: any) => opt.value === formData.course) || null}
                        onChange={(val: any) => handleInputChange("course", val?.value || "")}
                      />
                      {errors.course && (
                        <div className="text-danger small mt-1">{errors.course}</div>
                      )}
                    </div>

                    {/* Center — Super Admin only */}
                    {isSuperAdmin && (
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Center <span className="text-danger">*</span>
                        </label>
                        <CommonSelect
                          className={`select ${errors.center_id ? "is-invalid" : ""}`}
                          placeholder="Select Center"
                          options={centerOptions}
                          value={centerOptions.find((opt: any) => opt.value === formData.center_id) || null}
                          onChange={(val: any) => handleInputChange("center_id", val?.value || "")}
                        />
                        {errors.center_id && (
                          <div className="text-danger small mt-1">{errors.center_id}</div>
                        )}
                      </div>
                    )}

                    {/* Batch */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Batch Timing <span className="text-danger"></span>
                      </label>
                      <CommonSelect
                        className={`select ${errors.batch_id ? "is-invalid" : ""}`}
                        placeholder={isBatchTimingsLoading ? "Loading..." : "Select Batch"}
                        options={batchTimingOptions}
                        value={batchTimingOptions.find((opt: any) => opt.value === formData.batch_id) || null}
                        onChange={(val: any) => handleInputChange("batch_id", val?.value || "")}
                        isDisabled={!formData.course || (isSuperAdmin && !formData.center_id)}
                      />
                      {errors.batch_id && (
                        <div className="text-danger small mt-1">{errors.batch_id}</div>
                      )}
                    </div>

                    {/* Phone */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Phone <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="Enter phone number"
                        value={formData.phone}
                        onChange={(e) => handleInputChange("phone", e.target.value)}
                      />
                      {errors.phone && (
                        <div className="invalid-feedback">{errors.phone}</div>
                      )}
                    </div>

                    {/* Class */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Class <span className="text-danger">*</span>
                      </label>
                      <CommonSelect
                        className={`select ${errors.class_name ? "is-invalid" : ""}`}
                        placeholder="Select Class"
                        options={classOptions}
                        value={classOptions.find((opt: any) => opt.value === formData.class_name) || null}
                        onChange={(val: any) => handleInputChange("class_name", val?.value || "")}
                      />
                      {errors.class_name && (
                        <div className="invalid-feedback">{errors.class_name}</div>
                      )}
                    </div>

                    {/* Date of Join */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Date of Join <span className="text-muted">(Optional)</span>
                      </label>
                      <div className="position-relative">
                        <div className="input-icon position-relative">
                          <DatePicker
                            className="form-control datetimepicker"
                            style={{ backgroundImage: "none" }}
                            format="DD-MM-YYYY"
                            value={formData.date_of_join ? dayjs(formData.date_of_join) : null}
                            onChange={(date) => {
                              const formattedDate = date?.format("YYYY-MM-DD") || "";
                              handleInputChange("date_of_join", formattedDate);
                            }}
                            placeholder="Select Date"
                            popupStyle={DATE_PICKER_POPUP_STYLE}
                            disabledDate={(current) => disableDojDates(current, isSuperAdmin)}
                          />
                          <span className="input-icon-addon">
                            <i className="ti ti-calendar" />
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Photo */}
                    <div className="col-md-12 mb-3">
                      <PhotoCaptureCropper
                        photo={photo}
                        existingPhotoUrl={null}
                        onPhotoChange={(file) => {
                          setPhoto(file);
                        }}
                        error={""}
                        required={false}
                      />
                    </div>

                    {/* Actions */}
                    <div className="col-md-12 text-end mt-3">
                      <Link to={routes.studentList} className="btn btn-light me-2">
                        Cancel
                      </Link>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isRegistering || isSendingWhatsapp}
                      >
                        {isRegistering ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Registering...
                          </>
                        ) : isSendingWhatsapp ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            Sending Message...
                          </>
                        ) : (
                          "Register Student"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StarterKitNoticeModal
        show={showKitNoticeModal}
        studentName={registeredStudentName}
        courseName={registeredCourseName}
        message={kitNoticeMessage}
        onProceed={() => {
          setShowKitNoticeModal(false);
          if (registrationLink) {
            setShowShareModal(true);
          } else {
            navigate(routes.studentList);
          }
        }}
        onGoToList={() => {
          setShowKitNoticeModal(false);
          navigate(routes.studentList);
        }}
      />

      <ShareLinkModal
        show={showShareModal}
        registrationLink={registrationLink}
        kitPending={isKitPending}
        onClose={() => {
          setShowShareModal(false);
          navigate(routes.studentList);
        }}
      />
    </div>
  );
};

export default QuickRegister;
